/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * Lightweight main-image navigator for store product cards.
 */
import React, { useEffect, useMemo, useState } from "react";
import { buildImageUrl } from "../theme1/utils/theme1Helpers";

function Chevron({ dir }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {dir === "left" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

const VARIANT_STYLES = {
  light: {
    button: {
      background: "rgba(255,255,255,0.94)",
      color: "#2d3436",
      border: "1px solid rgba(15,23,42,0.06)",
      boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
    },
    dot: "#2d3436",
    imgFilter: undefined,
  },
  dark: {
    button: {
      background: "rgba(255,255,255,0.12)",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.2)",
      boxShadow: "0 8px 22px rgba(0,0,0,0.28)",
      backdropFilter: "blur(10px)",
    },
    dot: "#fff",
    imgFilter: undefined,
  },
  theme3: {
    button: {
      background: "#ffffff",
      color: "#12213f",
      border: "1px solid #e4edf4",
      boxShadow: "0 8px 18px rgba(17, 39, 88, 0.12)",
    },
    dot: "#12213f",
    imgFilter: undefined,
  },
};

export default function MainImageCarousel({
  images = [],
  alt = "",
  variant = "light",
  compact = false,
  children,
  imgClassName = "",
  imgStyle,
  className = "",
  style,
  onImageClick,
}) {
  const urls = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []).slice(0, 3),
    [images]
  );
  const [index, setIndex] = useState(0);
  const look = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  const canNav = urls.length > 1;
  const size = compact ? 26 : 32;

  useEffect(() => {
    setIndex(0);
  }, [urls.join("|")]);

  useEffect(() => {
    if (urls.length && index >= urls.length) setIndex(0);
  }, [urls.length, index]);

  const go = (dir, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!canNav) return;
    setIndex((current) => (current + dir + urls.length) % urls.length);
  };

  const stopCardClick = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
  };

  const src = urls[index]
    ? buildImageUrl(urls[index])
    : "https://placehold.co/300x200?text=No+Image";

  return (
    <div
      className={`main-image-carousel ${className}`.trim()}
      style={{ position: "relative", width: "100%", height: "100%", ...style }}
      onClick={onImageClick}
    >
      <img
        src={src}
        alt={alt}
        className={imgClassName}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          ...imgStyle,
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://placehold.co/300x200?text=No+Image";
        }}
      />

      {canNav && (
        <>
          <button
            type="button"
            className="main-image-nav main-image-nav-prev"
            aria-label="الصورة السابقة"
            onClick={(e) => go(-1, e)}
            onMouseDown={stopCardClick}
            onPointerDown={stopCardClick}
            style={{
              position: "absolute",
              top: "50%",
              left: compact ? 6 : 8,
              transform: "translateY(-50%)",
              width: size,
              height: size,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              zIndex: 4,
              padding: 0,
              transition: "transform 0.2s ease, opacity 0.2s ease",
              ...look.button,
            }}
          >
            <Chevron dir="left" />
          </button>
          <button
            type="button"
            className="main-image-nav main-image-nav-next"
            aria-label="الصورة التالية"
            onClick={(e) => go(1, e)}
            onMouseDown={stopCardClick}
            onPointerDown={stopCardClick}
            style={{
              position: "absolute",
              top: "50%",
              right: compact ? 6 : 8,
              transform: "translateY(-50%)",
              width: size,
              height: size,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              zIndex: 4,
              padding: 0,
              transition: "transform 0.2s ease, opacity 0.2s ease",
              ...look.button,
            }}
          >
            <Chevron dir="right" />
          </button>
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: compact ? 8 : 10,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 5,
              zIndex: 4,
              pointerEvents: "none",
            }}
          >
            {urls.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === index ? 14 : 6,
                  height: 6,
                  borderRadius: 99,
                  background: look.dot,
                  opacity: i === index ? 0.9 : 0.28,
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </div>
        </>
      )}

      {children}
    </div>
  );
}
